import { useAsyncBoundOperation } from "@/hooks/useApi";
import { IMAGE_MIME_TYPE_TO_EXTENSION, VIDEO_MIME_TYPE_TO_EXTENSION } from "@/constants/media";
import _ from "lodash";
const testImageMediaSupport = async () => {
    const formats = Object.entries(IMAGE_MIME_TYPE_TO_EXTENSION);
    const results = await Promise.all(
        formats.map(([, { ext, testFile }]) => {
            return new Promise<boolean>((resolve) => {
                if (!testFile) return resolve(false);
                const img = new Image();
                img.onload = () => resolve(true);
                img.onerror = () => resolve(false);
                img.src = testFile;
            });
        })
    );

    const formatsSupported = formats.filter((_, index) => results[index]);
    const imageMimeTypesSupported = formatsSupported.map(([mime]) => mime);
    const imageExtensionsSupported = _.uniq(formatsSupported.map(([, { ext }]) => ext));
    return { imageMimeTypesSupported, imageExtensionsSupported };
};

const testVideoMediaSupport = async () => {
    // check each SUPPORTED_VIDEO_MIME_TYPES if this browser can play
    const video = document.createElement("video");
    const videoMimeTypesSupported = [
        ...Object.keys(VIDEO_MIME_TYPE_TO_EXTENSION).filter((mime) => {
            return video.canPlayType(mime) !== "";
        })
    ];
    video.remove();

    const videoExtensionsSupported: string[] = _.uniq(
        videoMimeTypesSupported.map((mime) => VIDEO_MIME_TYPE_TO_EXTENSION[mime].ext)
    );

    return { videoMimeTypesSupported, videoExtensionsSupported };
};

let cachedMediaSupport: {
    videoMimeTypesSupported: string[];
    videoExtensionsSupported: string[];
    imageMimeTypesSupported: string[];
    imageExtensionsSupported: string[];
    allMimeTypesSupported: string[];
    allExtensionsSupported: string[];
} | null = null;

export const getTestMediaSupport = async () => {
    if (cachedMediaSupport) {
        return cachedMediaSupport;
    }

    const [videoSupport, imageSupport] = await Promise.all([
        testVideoMediaSupport(),
        testImageMediaSupport()
    ]);

    const { videoMimeTypesSupported = [], videoExtensionsSupported = [] } = videoSupport;
    const { imageMimeTypesSupported = [], imageExtensionsSupported = [] } = imageSupport;

    cachedMediaSupport = {
        videoMimeTypesSupported,
        videoExtensionsSupported,
        imageMimeTypesSupported,
        imageExtensionsSupported,
        allMimeTypesSupported: [...videoMimeTypesSupported, ...imageMimeTypesSupported],
        allExtensionsSupported: [...videoExtensionsSupported, ...imageExtensionsSupported]
    };

    return cachedMediaSupport;
};

export const useTestMediaSupport = () => {
    return useAsyncBoundOperation(getTestMediaSupport, { autoInvoke: true });
};
